<!--
Staff

Renders the form (right side) of Staff Directory to add/edit/delete users.
Opening an existing user will also show their SWAPP Location History and allows Admins to SWAPP users in and out.
-->
<template>
    <div style="width: 100%">

        <!--Header-->
        <v-row class="grey lighten-2 ma-0 pa-4 d-flex align-center white" no-gutters>

            <!--Username and Status-->
            <div class="ml-2 mr-4">

                <!--Pending-->
                <app-text v-if="form.userStatus === 'PENDING'"
                          :class="nameAndMessage.color + '--text'">
                    <v-icon class="icons8-circle mb-1 mr-2" color="accent" size="32"/>
                    {{ nameAndMessage.message }}
                </app-text>

                <!--Approved-->
                <app-text v-if="form.userStatus === 'APPROVED'" class="mt-1">
                    <v-icon class="icons8-checkmark-yes mb-1 mr-2" color="success" size="32"/>
                    {{ form.userName }}
                </app-text>

                <!--Suspended-->
                <app-text v-if="form.userStatus === 'SUSPENDED'" class="mt-1">
                    <v-icon class="icons8-private mb-1 mr-2" color="error" size="32"/>
                    {{ form.userName }}
                </app-text>

                <!--Archived-->
                <app-text v-if="form.userStatus === 'ARCHIVED'" class="mt-1">
                    <v-icon class="icons8-xbox-a mb-1 mr-2" color="grey" size="32"/>
                    {{ form.userName }}
                </app-text>

            </div>

            <v-spacer/>

            <!--User status action buttons-->
            <v-col v-if="form.userStatus === 'PENDING'"
                   :cols="$vuetify.breakpoint.width < 600 ? 12 : 6"
                   :class="$vuetify.breakpoint.width < 600 && 'mt-4'">
                <div class="d-flex justify-end">

                    <!--Approve-->
                    <app-btn v-if="form.userStatus === 'PENDING'"
                             @click.native="updateItemStatus('APPROVED')"
                             class="mr-4"
                             color="success"
                             icon="icons8-checkmark-yes"
                             label="Approve"/>

                    <v-spacer v-if="$vuetify.breakpoint.width < 600"/>

                    <!--Reject-->
                    <app-btn v-if="form.userStatus === 'PENDING'"
                             @click.native="updateItemStatus('REJECTED')"
                             class="ma-0"
                             color="error"
                             icon="icons8-multiply"
                             label="Reject"/>

                    <!--Suspend-->
                    <!--<appbutton v-if="form.userStatus !== 'SUSPENDED' && formMode !== 'Edit' && formMode !== 'New'"
                    label="Suspend" icon="icons8-private" type="error" class="ma-0" @click.native="updateItemStatus('SUSPENDED')" showlabel="xlOnly" />-->

                    <!--Restore-->
                    <appbutton v-if="form.userStatus === 'SUSPENDED' && formMode !== 'Edit' && formMode !== 'New'"
                               label="Restore" icon="icons8-process" type="success" class="ma-0"
                               @click.native="updateItemStatus('APPROVED')" showlabel="xlOnly"/>

                </div>
            </v-col>

            <!--Action buttons (edit | delete)-->
            <div v-if="['overview', 'sitePermit'].includes(tabs) && form.userStatus !== 'PENDING'"
                 class="d-flex align-center">

                <!--Edit-->
                <v-icon v-if="formMode !== 'New'"
                        @click.native="editItem()"
                        class="icons8-edit"
                        :class="formReadOnly ? 'frc-icon' : 'frc-icon-edit-active'"
                        :color="formReadOnly ? 'warning' : 'white'"
                        size="32"/>

                <!--Delete-->
                <v-icon v-if="formMode !== 'New' && form.delete === undefined"
                        @click.native="deleteItem(form.id)"
                        class="icons8-trash frc-icon"
                        color="error"
                        size="32"/>

            </div>

        </v-row>

        <!--Page content-->
        <v-row class="mt-0" no-gutters>
            <v-col cols="12">

                <!--Tabs (Overview | Location History)-->
                <v-tabs class="border-bottom-grey"
                        :height="buttonSizeDefault"
                        v-model="tabs">

                    <v-tabs-slider color="accent"/>

                    <!--Overview-->
                    <v-tab href="#overview">
                        <app-text category="text-small">Overview</app-text>
                    </v-tab>

                    <!--Location History-->
                    <v-tab v-if="formMode !== 'New'" href="#location">
                        <app-text category="text-small">Location History</app-text>
                    </v-tab>

                    <!--Schedule-->
                    <v-tab v-if="!['CU'].includes(form.userLevel)" href="#schedule">
                        <app-text category="text-small">Schedule</app-text>
                    </v-tab>

                    <!--Site Permit-->
                    <v-tab v-if="['CU'].includes(form.userLevel)" href="#sitePermit">
                        <app-text category="text-small">Site Permit</app-text>
                    </v-tab>

                </v-tabs>

                <!--Tabs Content (Overview | Location History | Schedule | Site Permit (Contractors only))-->
                <v-tabs-items v-model="tabs" class="appbackground">

                    <!--Overview tab content-->
                    <v-tab-item value="overview" class="pa-0">

                        <!--Description | Action buttons-->
                        <v-row class="pa-4" no-gutters>

                            <!-- Description -->
                            <v-col class="pa-0" cols="12">

                                <!--Info-->
                                <app-text>
                                    Use the form below to Edit Staff details or change the type, roll or user
                                    status.
                                </app-text>

                                <v-divider class="mt-4"/>

                            </v-col>

                            <!-- Action Buttons -->
                            <v-col class="d-flex">

                                <!--Undelete-->
                                <appbutton v-if="formMode !== 'New' && form.delete !== undefined"
                                           label="Undelete" icon="icons8-process" class="mr-2" type="grey"
                                           @click.native="undeleteItem(form.id)" :na="formMode !== 'View'"
                                           :class="{ disabled: formMode !== 'View' }" showlabel="xlOnly"/>

                            </v-col>

                        </v-row>

                        <!--Form-->
                        <v-row class="ma-0 px-4" no-gutters>

                            <!--Profile Picture | User Details-->
                            <v-row no-gutters style="width: 100%">

                                <!--Profile picture-->
                                <v-col
                                    class="d-flex justify-center align-center pa-2 rounded-lg white profileImage-container"
                                    :class="$vuetify.breakpoint.width >= 600 ? 'flex-grow-0 mr-4' : ''"
                                    :cols="$vuetify.breakpoint.width < 600 && 12"
                                    style="height: 272px; width: 272px">

                                    <!--If an image is present, render it-->
                                    <div v-if="form.profilePicFileURL && !tempImage"
                                         class="d-flex flex-column align-center">

                                        <!--Image-->
                                        <v-img :src="form.profilePicFileURL"
                                               class="rounded-lg" cover height="254" width="254"/>

                                        <!--Upload button-->
                                        <div v-if="formMode === 'Edit'"
                                             style="position: absolute; z-index: 9; margin-top: 200px">

                                            <photoupload class="mr-n4"
                                                         allowedtypes="image/*"
                                                         :docLink="{collection: 'users', documentId: form.id}"
                                                         folder="users-profile-pictures"/>

                                        </div>

                                    </div>

                                    <!--If a tempImage (upload preview) is present, render it-->
                                    <div v-else-if="tempImage"
                                         class="d-flex flex-column align-center">

                                        <!--Image-->
                                        <v-img :src="tempImage"
                                               class="rounded" cover height="216" width="216"/>

                                        <!--Upload button-->
                                        <div style="position: absolute; z-index: 9; margin-top: 152px">

                                            <photoupload class="mr-n4"
                                                         allowedtypes="image/*"
                                                         :docLink="{collection: 'users', documentId: form.id}"
                                                         folder="users-profile-pictures"/>

                                        </div>

                                    </div>

                                    <!--If neither an image or tempImage is present, render an icon-->
                                    <div v-else class="d-flex flex-column align-center" style="width: 216px">

                                        <!--Image-->
                                        <v-icon class="icons8-customer" size="216"/>

                                        <!--Upload button-->
                                        <div v-if="formMode === 'New' || formMode === 'Edit'"
                                             style="position: absolute; z-index: 9; margin-top: 152px">

                                            <photoupload class="mr-n4"
                                                         style="width: 100%"
                                                         allowedtypes="image/*"
                                                         :docLink="{collection: 'users', documentId: form.id}"
                                                         folder="users-profile-pictures"/>

                                        </div>

                                    </div>

                                </v-col>

                                <!-- User details -->
                                <v-col :class="$vuetify.breakpoint.width >= 600 && 'flex-grow-1'"
                                       :cols="$vuetify.breakpoint.width < 600 && 12">

                                    <!--Name-->
                                    <app-input input-type="textInput"
                                               :class="$vuetify.breakpoint.width < 600 && 'mt-4'"
                                               :error="errors.userName"
                                               :error-messages="errors.userNameErrorMessage"
                                               :is-form-read-only="formReadOnly"
                                               label="Full Name"
                                               v-model.trim="form.userName"/>

                                    <!--Position-->
                                    <app-input input-type="textInput"
                                               class="mt-4"
                                               :error="errors.userPosition"
                                               :error-messages="errors.userPositionErrorMessage"
                                               :is-form-read-only="formReadOnly"
                                               label="Position"
                                               v-model.trim="form.userPosition"/>

                                    <!--Email-->
                                    <app-input input-type="textInput"
                                               class="mt-4"
                                               :error="errors.userEmail"
                                               :error-messages="errors.userEmailErrorMessage"
                                               :is-form-read-only="formReadOnly"
                                               label="Email"
                                               v-model.trim="form.userEmail"/>

                                    <!--Telephone-->
                                    <app-input input-type="textInput"
                                               class="mt-4"
                                               :error="errors.userTelephone"
                                               :error-messages="errors.userTelephoneErrorMessage"
                                               :is-form-read-only="formReadOnly"
                                               label="Telephone"
                                               v-model="form.userTelephone"/>

                                </v-col>

                            </v-row>

                            <!-- DOB -->
                            <v-col class="mt-4" :class="$vuetify.breakpoint.width >= 600 && 'pr-2'"
                                   cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <date-picker v-on:emitDate="handleUserDOBPicker"
                                             :date="form.userDOB"
                                             :isFormReadOnly="formReadOnly"
                                             label="Date Of Birth"
                                             :minDate="[100, 'years', 'past']"
                                             :maxDate="[16, 'years', 'past']"/>
                            </v-col>

                            <!-- Start Date -->
                            <v-col class="mt-4" :class="$vuetify.breakpoint.width >= 600 && 'pl-2'"
                                   cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <date-picker v-on:emitDate="handleUserStartDatePicker"
                                             :date="form.userStartDate"
                                             :isFormReadOnly="formReadOnly"
                                             label="Start Date"
                                             :minDate="[50, 'years', 'past']"
                                             :maxDate="[0, 'years', 'future']"/>
                            </v-col>

                            <!-- User Category -->
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <app-input input-type="select"
                                           class="rounded-lg mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'pr-2'"
                                           :is-form-read-only="formReadOnly"
                                           :items="userCategoriesOptionsData"
                                           item-text="text"
                                           item-value="value"
                                           label="User Category"
                                           v-model="form.userCategory">
                                    <template v-slot:item="data">
                                        <app-text category="text-default">{{ data.item }}</app-text>
                                    </template>
                                </app-input>
                            </v-col>

                            <!-- Primary Work Location -->
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <app-input input-type="select"
                                           class="rounded-lg mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'pl-2'"
                                           :error="errors.usualSite"
                                           :error-messages="errors.usualSiteErrorMessage"
                                           :is-form-read-only="formReadOnly"
                                           :items="sitesData"
                                           item-text="siteName"
                                           label="Primary Work Location"
                                           v-model="form.usualSite">
                                    <template v-slot:item="data">
                                        <app-text category="text-default">{{ data.item.siteName }}</app-text>
                                    </template>
                                </app-input>
                            </v-col>

                            <!--Position-->
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <app-input input-type="textInput"
                                           class="mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'pr-2'"
                                           :is-form-read-only="formReadOnly"
                                           label="Player Position"
                                           v-model="form.playerPosition"/>
                            </v-col>

                            <!-- Address -->
                            <v-col class="mt-4" cols="12">
                                <app-text category="text-medium" class="darkgrey--text">Address</app-text>
                                <v-divider class="mt-2"/>
                            </v-col>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <app-input input-type="textInput"
                                           class="mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'pr-2'"
                                           :is-form-read-only="formReadOnly"
                                           label="Address Line 1"
                                           v-model="form.userAddress.userAddressLine1"/>
                            </v-col>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <app-input input-type="textInput"
                                           class="mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'pl-2'"
                                           :is-form-read-only="formReadOnly"
                                           label="Address Line 2"
                                           v-model="form.userAddress.userAddressLine2"/>
                            </v-col>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <app-input input-type="textInput"
                                           class="mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'pr-2'"
                                           :is-form-read-only="formReadOnly"
                                           label="Address Line 3"
                                           v-model="form.userAddress.userAddressLine3"/>
                            </v-col>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <app-input input-type="textInput"
                                           class="mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'pl-2'"
                                           :is-form-read-only="formReadOnly"
                                           label="Town"
                                           v-model="form.userAddress.userTown"/>
                            </v-col>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <app-input input-type="textInput"
                                           class="mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'pr-2'"
                                           :is-form-read-only="formReadOnly"
                                           label="County"
                                           v-model="form.userAddress.userCounty"/>
                            </v-col>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <app-input input-type="textInput"
                                           class="mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'pl-2'"
                                           :error="errors.userPostcode"
                                           :error-messages="errors.userPostcodeErrorMessage"
                                           :is-form-read-only="formReadOnly"
                                           label="Postcode"
                                           v-model="form.userAddress.userPostcode"/>
                            </v-col>

                            <!-- Next of Kin -->
                            <v-col class="mt-4" cols="12">
                                <app-text category="text-medium" class="darkgrey--text">Next of Kin</app-text>
                                <v-divider class="mt-2"/>
                            </v-col>
                            <v-col cols="12" xs="12" sm="4" md="4" lg="4" xl="4">
                                <app-input input-type="textInput"
                                           class="mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'pr-2'"
                                           :error="errors.NOKName"
                                           :error-messages="errors.NOKNameErrorMessage"
                                           :is-form-read-only="formReadOnly"
                                           label="Name"
                                           v-model="form.NOKName"/>
                            </v-col>
                            <v-col cols="12" xs="12" sm="4" md="4" lg="4" xl="4">
                                <app-input input-type="textInput"
                                           class="mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'px-2'"
                                           :is-form-read-only="formReadOnly"
                                           label="Relationship"
                                           v-model="form.NOKRelationship"/>
                            </v-col>
                            <v-col cols="12" xs="12" sm="4" md="4" lg="4" xl="4">
                                <app-input input-type="textInput"
                                           class="mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'pl-2'"
                                           :error="errors.NOKTelNo"
                                           :error-messages="errors.NOKTelNoErrorMessage"
                                           :is-form-read-only="formReadOnly"
                                           label="Telephone Number"
                                           type="number"
                                           v-model="form.NOKTelNo"/>
                            </v-col>

                            <!--Access & Abilities | Observation Responsibilities-->
                            <v-col class="mt-4" cols="12">
                                <app-text category="text-medium" class="darkgrey--text">Access, Abilities and
                                    Observation Responsibilities
                                </app-text>
                                <v-divider class="mt-2"/>
                            </v-col>
                            <v-col cols="12" xs="12" sm="6">
                                <app-input input-type="select"
                                           chips
                                           class="mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'pr-2'"
                                           deletable-chips
                                           :is-form-read-only="formReadOnly"
                                           :items="userAccessAbilitiesOptionsData"
                                           label="Access and Abilities"
                                           multiple
                                           small-chips
                                           v-model="form.userAccessAndAbilities"/>
                            </v-col>
                            <v-col cols="12" xs="12" sm="6">
                                <app-input input-type="select"
                                           chips
                                           class="mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'pl-2'"
                                           deletable-chips
                                           :is-form-read-only="formReadOnly"
                                           :items="observationTypesOptionsData"
                                           label="Observation Responsibilities"
                                           multiple
                                           small-chips
                                           v-model="form.userObservationResponsibilities"/>
                            </v-col>

                            <!-- Configuration (User Type | User Role | User Status) -->
                            <v-col class="mt-4" cols="12">
                                <app-text category="text-medium" class="darkgrey--text">Configuration</app-text>
                                <v-divider class="mt-2"/>
                            </v-col>

                            <!--User type-->
                            <v-col cols="12" xs="12" sm="4" md="4" lg="4" xl="4">
                                <app-input input-type="select"
                                           class="mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'pr-2'"
                                           :is-form-read-only="formReadOnly"
                                           :items="userTypesOptionsData"
                                           label="User type"
                                           v-model="form.userType">
                                    <template v-slot:item="data">
                                        <app-text category="text-default">{{ data.item }}</app-text>
                                    </template>
                                </app-input>
                            </v-col>

                            <!--User role-->
                            <v-col cols="12" xs="12" sm="4" md="4" lg="4" xl="4">
                                <app-input input-type="select"
                                           class="mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'px-2'"
                                           :is-form-read-only="formReadOnly"
                                           :items="computedUserRoles"
                                           label="User Role"
                                           v-model="form.userRole">
                                    <template v-slot:item="data">
                                        <app-text category="text-default">{{ data.item }}</app-text>
                                    </template>
                                </app-input>
                            </v-col>

                            <!--User status-->
                            <v-col cols="12" xs="12" sm="4" md="4" lg="4" xl="4">
                                <app-input input-type="select"
                                           class="mt-4"
                                           :class="$vuetify.breakpoint.width >= 600 && 'pl-2'"
                                           :is-form-read-only="formReadOnly"
                                           :items="userStatusOptionsData"
                                           label="User Status"
                                           v-model="form.userStatus">
                                    <template v-slot:item="data">
                                        <app-text category="text-default">{{ data.item }}</app-text>
                                    </template>
                                </app-input>
                            </v-col>

                            <!--Save-->
                            <v-col cols="12" class="d-flex justify-end mt-4">
                                <app-btn v-if="formMode === 'Edit' || formMode === 'New'"
                                         @click.native="validateForm"
                                         color="success"
                                         icon="icons8-save"
                                         label="Save"/>
                            </v-col>

                        </v-row>

                        <!--Created and Modified info -->
                        <v-col v-if="formMode !== 'New'" class="mb-4" cols="12">

                            <v-divider/>

                            <div>
                                <app-text category="text-small-bold" class="grey--text mt-4">
                                    Created:
                                    <span class="font-weight-regular">
                                        {{ form.createdUserData.userName }}
                                        -
                                        {{ MIX_formatDateTime(form.createdDateTime, "x", "DD-MMM-YYYY") }}
                                    </span>
                                    |
                                    Modified:
                                    <span class="font-weight-regular">
                                        {{ form.modifiedUserData.userName }}
                                        -
                                        {{ MIX_formatDateTime(form.modifiedDateTime, "x", "DD-MMM-YYYY") }}
                                    </span>
                                </app-text>
                            </div>

                        </v-col>

                    </v-tab-item>

                    <!--Location History tab content-->
                    <v-tab-item value="location" class="pa-0">

                        <!--Location History-->
                        <v-row class="pa-4" no-gutters>

                            <!--Instructional text-->
                            <v-col cols="12">

                                <!--User is currently SWAPPed [in/out], message-->
                                <app-text category="text-default">
                                    <span class="font-weight-bold">{{ form.userName }}</span>
                                    is currently SWAPPed
                                    <span class="font-weight-bold">{{ form.swappStatus === 1 ? 'In' : 'Out' }}</span>
                                </app-text>

                                <!--SWAPP button-->
                                <app-btn v-if="!modifySwappStatus"
                                         @click.native="swappUser()"
                                         :block="$vuetify.breakpoint.width < 600"
                                         class="mt-4"
                                         color="primary"
                                         icon="icons8-qr-code"
                                         :label="`Swapp ${ form.userName.split(' ')[0] } ${ form.swappStatus === 1 ? 'Out' : 'In' }`"/>

                                <!--Cancel button-->
                                <app-btn v-if="modifySwappStatus"
                                         @click.native="swappUser()"
                                         :block="$vuetify.breakpoint.width < 600"
                                         class="my-4"
                                         color="grey"
                                         icon="icons8-cancel"
                                         label="Cancel"/>

                            </v-col>

                            <!--Form-->
                            <v-row v-if="modifySwappStatus" no-gutters>

                                <!--Site-->
                                <v-col :class="$vuetify.breakpoint.width < 600 ? '' : 'pr-2'"
                                       cols="12" xs="12" sm="6">
                                    <v-autocomplete @change="selectSiteLocation()"
                                                    background-color="white"
                                                    class="rounded-lg"
                                                    filled
                                                    flat
                                                    hide-details
                                                    :items="availableSites"
                                                    item-text="siteName"
                                                    :label="$t('fields.lastSite')"
                                                    return-object
                                                    outlined
                                                    v-model="selectedSite"/>

                                </v-col>

                                <!--Last location-->
                                <v-col :class="$vuetify.breakpoint.width < 600 ? 'mt-4' : 'pl-2'"
                                       cols="12" xs="12" sm="6">
                                    <v-autocomplete
                                        @change="selectLocation()"
                                        background-color="white"
                                        class="rounded-lg"
                                        filled
                                        flat
                                        hide-details
                                        :items="availableLocations"
                                        item-text="locationName"
                                        :label="$t('fields.lastLocation')"
                                        no-data-text="No Locations Found"
                                        outlined
                                        return-object
                                        v-model="selectedLocation"/>

                                </v-col>

                                <!--Update SWAPP status button-->
                                <v-col class="d-flex mt-4">

                                    <v-spacer/>

                                    <!--Update SWAPP status-->
                                    <app-btn v-if="modifySwappStatus"
                                             @click.native="updateSwappStatus()"
                                             :block="$vuetify.breakpoint.width < 600"
                                             color="success"
                                             :disabled="selectedLocation === null"
                                             icon="icons8-qr-code"
                                             :label="`Swapp ${ form.userName.split(' ')[0] } ${ form.swappStatus === 1 ? 'Out' : 'In' }`"/>

                                </v-col>

                            </v-row>

                        </v-row>

                        <!--Table - Render a table displaying a user's location history-->
                        <v-col cols="12">

                            <!--Title-->
                            <app-text category="text-medium" class="darkgrey--text">
                                Location History
                            </app-text>

                            <v-divider class="mt-2"/>

                            <!--Table-->
                            <v-data-table :headers="locationsHistoryHeaders"
                                          :items="filteredLocations"
                                          :items-per-page="5"
                                          class="rounded-lg mt-4">

                                <!--Render SWAPP status-->
                                <template v-slot:[`item.swappStatus`]="{ item }">
                                    <v-chip class="success" small v-if="item.swappStatus === 1">IN</v-chip>
                                    <v-chip class="lightgrey" small v-else>OUT</v-chip>
                                </template>

                                <!--Render SWAPP site-->
                                <template v-slot:[`item.site.siteName`]="{ item }">
                                    <app-text category="text-default">
                                        {{ MIX_capitalizeText(item.site.siteName) }}
                                    </app-text>
                                </template>

                                <!--Render SWAPP location-->
                                <template v-slot:[`item.location.locationName`]="{ item }">
                                    <app-text category="text-default">
                                        {{ MIX_capitalizeText(item.location.locationName) }}
                                    </app-text>
                                </template>

                                <!--Render SWAPP method-->
                                <template v-slot:[`item.swappMethod`]="{ item }">
                                    <!--<app-text category="text-default" class="justify-center">{{ MIX_capitalizeText(item.swappMethod) }}</app-text>-->
                                    <app-text category="text-default" class="justify-center">
                                        {{ item.swappMethod }}
                                    </app-text>
                                </template>

                                <!--Render SWAPP date-->
                                <template v-slot:[`item.createdDateTime`]="{ item }">
                                    <app-text category="text-default" class="justify-center">
                                        {{ MIX_formatDateTime(item.createdDateTime, "x", "ddd Do MMM YYYY @ HH:mm") }}
                                    </app-text>
                                </template>

                            </v-data-table>

                        </v-col>

                    </v-tab-item>

                    <!--Schedule tab content-->
                    <v-tab-item value="schedule" class="pa-0">
                        <user-schedule :user="form"/>
                    </v-tab-item>

                    <!--Site Permit tab content-->
                    <v-tab-item value="sitePermit" class="pa-0">

                        <!--Instructions-->
                        <app-text category="text-default" class="ma-4">
                            Complete the following form to create a new Site Permit.
                            The permit will need to be approved by Sixways before the user can SWAPP in.
                        </app-text>

                        <v-divider class="ma-4"/>

                        <!--Site Permit form-->
                        <site-permit v-on:emitCreatedSitePermit="emittedCreatedSitePermit"
                                     class="ma-4"
                                     :formMode="formMode"
                                     :user="form"/>

                    </v-tab-item>

                </v-tabs-items>

            </v-col>
        </v-row>

    </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex"
import DatePicker from "../modules/datePicker/datePicker_component"
import UserSchedule from "../views/userSchedule/userSchedule";

export default {

    name: "StaffDirectory",

    components: {DatePicker, UserSchedule},

    data: () => ({
        collection: "users",
        collectionTitle: "Staff Directory",
        collectionItem: "user",
        collectionItemTitle: "Staff Directory",
        tabs: "overview",

        // * FORM RELATED
        contractorCompany: {},
        errors: {
            userName: false,
            userNameErrorMessage: '',
            userPosition: false,
            userPositionErrorMessage: '',
            userEmail: false,
            userEmailErrorMessage: '',
            userTelephone: false,
            userTelephoneErrorMessage: '',

            userPostcode: false,
            userPostcodeErrorMessage: '',
            NOKName: false,
            NOKNameErrorMessage: '',
            NOKTelNo: false,
            NOKTelNoErrorMessage: '',
            usualSite: false,
            usualSiteErrorMessage: '',
        },
        formMode: "",
        formReadOnly: true,
        formBackground: "grey lighten-3",
        form: {
            id: '',
            authId: null,
            lastLocation: {
                locationId: '',
                locationName: '',
            },
            lastSite: {
                siteId: '',
                siteName: '',
            },
            NOKName: '',
            NOKRelationship: '',
            NOKTelNo: '',
            playerPosition: '',
            privacyPolicyConsent: false,
            profilePicFileURL: null,
            swappMethod: null, //default null
            swappStatus: 0, //default 0
            userAccessAndAbilities: [],
            userAddress: {
                userAddressLine1: '',
                userAddressLine2: '',
                userAddressLine3: '',
                userTown: '',
                userCounty: '',
                userPostcode: '',
            },
            userCategory: '',
            userDOB: '',
            userEmail: '',
            userLevel: '',
            userName: '',
            userObservationResponsibilities: [],
            userPosition: '',
            userRole: '',
            userStartDate: '',
            userStatus: '',
            userTelephone: '',
            userType: '',
            usualSite: '',

            // Added from admin
            createdUserData: {},
            createdDateTime: '',
            modifiedUserData: {},
            modifiedDateTime: '',
        },
        // * PROFILE PICTURE
        types: "image/*",
        storagePathProfilePic: "users-profile-pictures",
        photoUploadResult: {},
        tempImage: '',

        //check if user was created in admin dashboard
        userInLocalStorage: false,
        userInLocalStorageId: null,

        filterStateItems: ["CURRENT", "ARCHIVED"],

        // * SWAPP STATUS RELATED
        modifySwappStatus: false, // modify swapp status
        availableLocations: [], // locations from the db
        allowLocationSelect: false, // only allow location select once a site is selected
        availableSites: [], // sites from the db
        selectedSite: null, // select site from options
        selectedLocation: null, // select location from options,
        swappDirection: 0,
        locationsHistory: [],
        sitesData: []
    }),

    computed: {
        ...mapGetters({
            GET_openItem: "GET_openStaffDirectory",
            GET_panelLayout: "GET_panelLayout", // Panel Layout used for Layout Structure
            GET_openStaffDirectory: "GET_openStaffDirectory",
            GET_confirmBoxResult: "confirmBox_store/GET_confirmBoxResult", // Get Confirm Box Result
            GET_areas: "GET_areas", // Get Areas
            GET_photoUploadResult: 'photoUpload_store/GET_photoUploadResult',
            GET_lookupValues: 'GET_lookupValues',
        }),

        /**
         * Filtered Locations
         *
         * @returns {*}
         */
        filteredLocations() {
            const t = this

            return t.MIX_sortByCreatedDateTime(t.locationsHistory)
        },

        locationsHistoryHeaders() {
            return [
                {text: "Status", value: "swappStatus", align: "center", sortable: false, hidden: false, hide: false},
                {text: "Site", value: "site.siteName", align: "left", sortable: false, hidden: false, hide: false},
                {
                    text: "Location",
                    value: "location.locationName",
                    align: "left",
                    sortable: false,
                    hidden: false,
                    hide: false
                },
                {text: "Method", value: "swappMethod", align: "center", sortable: false, hidden: false, hide: false},
                {
                    text: "Swapped at",
                    value: "createdDateTime",
                    align: "center",
                    sortable: false,
                    hidden: false,
                    hide: false
                },
            ];
        },

        /**
         * Computed User Roles
         *
         * Return the correct user roles for the specified user type.
         * @returns {Array} user roles as strings
         */
        computedUserRoles() {
            const t = this
            let roles = []
            const type = t.form.userType

            if (type === 'Staff') {
                roles = ['Admin', 'Manager', 'User']
            } else if (type === 'Contractor') {
                roles = ['User']
                t.form.userRole = 'User'
            } else if (type === 'Player') {
                roles = ['User']
                t.form.userRole = 'User'
            } else if (type === 'Visitor') {
                roles = ['User']
                t.form.userRole = 'User'
            } else if (type === 'Developer') {
                roles = ['User']
                t.form.userRole = 'User'
            }

            return roles
        },

        /**
         * Name And Message
         *
         * Return a message and color based on the userStatus and userType.
         * If the User is a Contractor and has a Contractor ID, return the User Name and Contractor Name.
         * If the User is a Contractor and does NOT have a Contractor ID, return a red warning message.
         *
         * @returns {{color: string, message: string}}
         */
        nameAndMessage() {
            const t = this
            let nameAndMessage = {
                message: t.form.userName,
                color: ''
            }

            if (t.form.userType === 'Contractor') {
                if (t.form.contractorCompanyId) {
                    nameAndMessage.message = `${t.form.userName} (${t.contractorCompany.contractorName})`
                    nameAndMessage.color = 'darkgrey'
                } else {
                    nameAndMessage.message = `This User is not associated with a Contractor`
                    nameAndMessage.color = 'error'
                }
            }

            return nameAndMessage
        },

    },

    methods: {
        ...mapActions({ACT_openItem: "ACT_openStaffDirectory", ACT_openStaffDirectory: "ACT_openStaffDirectory"}),

        /**
         * Get Sites Collection
         *
         * Clear the table data to avoid duplications.
         * Fetch all the data from the Sites collection.
         * Iterate over the collection and only push documents that aren't marked as deleted to the sitesData array.
         *
         * @returns {Promise<void>}
         */
        async getSitesCollectionData() {
            const t = this

            const collection = t.$firebase.db.collection('sites')
            collection.onSnapshot(snapshot => {

                // Clear the users data to avoid duplications
                t.sitesData = []

                snapshot.forEach(doc => {
                    const document = doc.data()
                    document.id = doc.id

                    // Only add documents that aren't marked as deleted
                    if (!document.hasOwnProperty('delete')) {
                        t.sitesData.push(document)
                    }

                })
            })
        },

        async getContractorsCollectionData() {
            const t = this

            const collection = t.$firebase.db.collection('contractors').doc(t.form.contractorCompanyId)
            const doc = await collection.get()

            // If the data exists
            if (doc.exists) {
                t.contractorCompany = doc.data()
            }

        },

        /**
         * Validate
         *
         * Validates the required fields for presence only.
         * If any of the fields are missing mark them in an errors object.
         * When there are no errors left, save the record.
         */
        validateForm() {
            const t = this
            const emailRegex = /.+@.+\..+/
            const postcodeRegex = /^[a-zA-Z]{1,2}[0-9]{1,2}[a-zA-Z]?[0-9][a-zA-Z]{2}$/

            t.errors.userName = false
            t.errors.userNameErrorMessage = ''
            t.errors.userPosition = false
            t.errors.userPositionErrorMessage = ''
            t.errors.userEmail = false
            t.errors.userEmailErrorMessage = ''
            t.errors.userTelephone = false
            t.errors.userTelephoneErrorMessage = ''
            t.errors.userPostcode = false
            t.errors.userPostcodeErrorMessage = ''
            t.errors.NOKName = false
            t.errors.NOKNameErrorMessage = ''
            t.errors.NOKTelNo = false
            t.errors.NOKTelNoErrorMessage = ''
            t.errors.usualSite = false
            t.errors.usualSiteErrorMessage = ''

            // Name
            if (!t.form.userName.trim()) {
                t.errors.userName = true
                t.errors.userNameErrorMessage = 'Name required'
            }
            // Must be between 2 and 60 characters
            else if (t.form.userName.trim().length < 2 || t.form.userName.trim().length > 60) {
                t.errors.userName = true
                t.errors.userNameErrorMessage = 'Name must be between 2 and 60 characters'
            }

            // Position
            if (!t.form.userPosition.trim()) {
                t.errors.userPosition = true
                t.errors.userPositionErrorMessage = 'Position required'
            }

            // Email
            if (!t.form.userEmail.trim()) {
                t.errors.userEmail = true
                t.errors.userEmailErrorMessage = 'Email required'
            }
            // Must be a (simple) valid email address
            else if (!emailRegex.test(t.form.userEmail)) {
                t.errors.userEmail = true
                t.errors.userEmailErrorMessage = 'Email not valid'
            }

            // Telephone
            if (!t.form.userTelephone.trim()) {
                t.errors.userTelephone = true
                t.errors.userTelephoneErrorMessage = 'Telephone Number required'
            }
            // Landline number must start 01, 02 or 03 and be either 10 or 11 digits
            else if (['1', '2', '3'].includes(t.form.userTelephone.trim()[1]) && (t.form.userTelephone.trim().length < 10 || t.form.userTelephone.trim().length > 11)) {
                t.errors.userTelephone = true
                t.errors.userTelephoneErrorMessage = 'Landline numbers must have either 10 or 11 digits'
            }
            // Mobile number must start 07 and be 11 digits
            else if (['7'].includes(t.form.userTelephone.trim()[1]) && t.form.userTelephone.trim().length !== 11) {
                t.errors.userTelephone = true
                t.errors.userTelephoneErrorMessage = 'Mobile numbers must have 11 digits'
            }
            // Number must start 01, 02, 03 or 07
            else if (!['0'].includes(t.form.userTelephone[0]) || ['0', '4', '5', '6', '8', '9'].includes(t.form.userTelephone[1])) {
                t.errors.userTelephone = true
                t.errors.userTelephoneErrorMessage = 'Landline numbers start 01, 02 or 03. Mobile numbers must start 07'
            }

            // Primary Work Location
            if (!t.form.usualSite) {
                t.errors.usualSite = true
                t.errors.usualSiteErrorMessage = 'Primary Work Location required'
            }

            // Postcode
            if (t.form.userAddress.userPostcode.trim() && !postcodeRegex.test(t.form.userAddress.userPostcode.trim())) {
                t.errors.userPostcode = true
                t.errors.userPostcodeErrorMessage = 'Postcode not valid'
            }

            // NOK Name
            // Must be between 2 and 60 characters
            if (t.form.NOKName && (t.form.NOKName.trim().length < 2 || t.form.NOKName.trim().length > 60)) {
                t.errors.NOKName = true
                t.errors.NOKNameErrorMessage = 'Name must be between 2 and 60 characters'
            }

            // NOK Telephone
            // Telephone
            if (t.form.NOKTelNo) {

                // Landline number must start 01, 02 or 03 and be either 10 or 11 digits
                if (['1', '2', '3'].includes(t.form.NOKTelNo.trim()[1]) && (t.form.NOKTelNo.trim().length < 10 || t.form.NOKTelNo.trim().length > 11)) {
                    t.errors.NOKTelNo = true
                    t.errors.NOKTelNoErrorMessage = 'Landline numbers must have either 10 or 11 digits'
                }
                // Mobile number must start 07 and be 11 digits
                else if (['7'].includes(t.form.NOKTelNo.trim()[1]) && t.form.NOKTelNo.trim().length !== 11) {
                    t.errors.NOKTelNo = true
                    t.errors.NOKTelNoErrorMessage = 'Mobile numbers must have 11 digits'
                }
                // Number must start 01, 02, 03 or 07
                else if (!['0'].includes(t.form.NOKTelNo[0]) || ['0', '4', '5', '6', '8', '9'].includes(t.form.NOKTelNo[1])) {
                    t.errors.NOKTelNo = true
                    t.errors.NOKTelNoErrorMessage = 'Landline numbers start 01, 02 or 03. Mobile numbers must start 07'
                }

            }

            // Check if there any errors left
            if (!Object.values(t.errors).includes(true)) {
                t.setUserLevel()
                t.saveItem()
            }
        },

        /**
         * Set User Level
         *
         * Configure the user's user level from their user type and user role.
         */
        setUserLevel() {
            const t = this

            t.form.userLevel = t.form.userType[0] + t.form.userRole[0]
        },

        async openItem() {
            const t = this

            const openItem = await t.GET_openItem

            switch (openItem.process) {
                case "New":
                    t.formMode = "New"
                    t.formReadOnly = false
                    t.formBackground = "white"
                    break
                case "View":
                    const item1 = await t.MIX_readDocumentById(t.collection, openItem.data.id)
                    t.form = item1.data
                    t.formMode = "View"
                    t.formReadOnly = true
                    t.formBackground = "grey lighten-3"
                    break
                case "Edit":
                    const item2 = await t.MIX_readDocumentById(t.collection, openItem.data.id)
                    t.form = item2.data
                    t.formMode = "Edit"
                    t.formReadOnly = false
                    t.formBackground = "white"
                    break
            }
        },

        async undeleteItem(id) {
            const t = this

            const undeleteResult = await t.MIX_undeleteDocument(t.collection, id)

            if (undeleteResult.code === 1) {
                t.MIX_alert(1, `${t.collectionItemTitle} Restored`, undeleteResult.data, null)
            } else {
                t.MIX_alert(-1, `Error Restoring ${t.collectionItemTitle}`, null, undeleteResult.error)
                console.log(`undeleteResult.error = ${JSON.stringify(undeleteResult.error, null, 2)}`)
            }

            await t.refreshItemView()
        },

        deleteItem(id) {
            const t = this
            const width = t.$vuetify.breakpoint.width <= 425 ? '' : '400'

            t.MIX_confirmBox({
                show: true,
                collection: t.collection,
                id: id,
                title: `Delete Staff Member`,
                description: `Are you sure you want to delete this staff member: <br /><div class="mt-4"><strong>${t.form[t.collectionItem + "Name"]}</strong>?</div>`,
                width,
                trueButtonText: "Yes",
                trueButtonValue: "YES",
                falseButtonText: "No",
                falseButtonValue: "NO",
            })
        },

        removeUploadedDocument() {
            this.$store.commit("upload_file_store/setFile", null)
        },

        editItem() {
            const t = this

            if (t.formMode === "View") {
                t.formMode = "Edit"
                t.formReadOnly = false
                t.formBackground = "white"
            } else if (t.formMode === "Edit") {
                t.formMode = "View"
                t.formReadOnly = true
                t.formBackground = "grey lighten-3"
                t.tempImage = ''
                t.removeUploadedDocument()
                t.refreshItemView()
            }

        },

        async checkUserInLocalStorage(email) {
            const t = this

            return new Promise(function (resolve, reject) {
                t.$firebase.db
                    .collection("users")
                    .where("userEmail", "==", email)
                    .get()
                    .then(function (querySnapshot) {
                        if (querySnapshot.docs.length === 0) {
                            t.userInLocalStorage = false
                            return resolve(t.userInLocalStorage)
                        } else {
                            t.userInLocalStorage = true
                            t.userInLocalStorageId = querySnapshot.docs[0].id
                            return resolve(t.userInLocalStorage)
                        }
                    })
                    .catch(function (error) {
                        return reject(error)
                    })
            })
        },

        async saveItem() {
            const t = this

            // If form is NEW
            if (t.formMode === "New") {

                // Check if user already exists
                const userExists = await this.checkUserInLocalStorage(t.form.userEmail)

                if (userExists !== true) {
                    const createDocumentResult = await t.MIX_createDocument(t.collection, t.form)

                    if (createDocumentResult.code === 1) {
                        t.MIX_alert(1, `User successfully created`, null, null)
                        t.formMode = "View"
                        t.formReadOnly = true
                        t.formBackground = "grey lighten-3"

                        // Add uploaded profile image
                        await t.uploadProfileImage()

                        await t.refreshItemView(createDocumentResult.data.id)

                        t.removeUploadedDocument()

                    } else {
                        t.MIX_alert(-1, `Error creating user`, null, null)
                    }
                } else {
                    t.MIX_alert(-1, `This user already exists`, null, null)
                }
            }

            // If form is EDIT
            else if (t.formMode === "Edit") {
                const updateDocumentResult = await this.MIX_updateDocument(t.collection, t.form)
                if (updateDocumentResult.code === 1) {
                    t.MIX_alert(1, `User successfully updated`, updateDocumentResult.data, null)
                    t.formMode = "View"
                    t.formReadOnly = true
                    t.formBackground = "grey lighten-3"

                    // Add uploaded profile image
                    await t.uploadProfileImage()

                    await t.refreshItemView()

                } else {
                    t.MIX_alert(-1, "Error updating user", null, updateDocumentResult.error)
                }
            }
        },

        async refreshItemView(id) {
            const t = this

            // If no ID has been passed through, use the form ID
            if (!id) {
                id = t.form.id
            }

            const itemResult = await t.MIX_readDocumentById(t.collection, id)
            const payload = {}
            payload.process = "View"
            payload.popup = false
            payload.data = itemResult.data
            t.ACT_openItem(payload)
            await t.openItem()
        },

        async updateItemStatus(status) {
            const t = this

            const updateResult = await t.MIX_updateDocumentFieldsById(t.collection, t.form.id, {[t.collectionItem + "Status"]: status})

            if (updateResult.code === 1) {
                t.MIX_alert(1, "User status successfully updated", updateResult.data, null);
                await t.refreshItemView();
            } else {
                t.MIX_alert(-1, "Error updating user status", null, updateResult.error);
            }
        },

        swappUser() {
            const t = this

            t.getAvailableSites()
            t.modifySwappStatus = !t.modifySwappStatus
        },

        selectSiteLocation() {
            const t = this

            if (t.selectedSite != null) {
                t.form.lastSite.siteId = t.selectedSite.id
                t.form.lastSite.siteName = t.selectedSite.siteName
            }
            t.getAvailableLocations()
            t.allowLocationSelect = true
        },

        selectLocation() {
            const t = this

            if (t.selectedLocation != null) {
                t.form.lastLocation.locationId = t.selectedLocation.id
                t.form.lastLocation.locationName = t.selectedLocation.locationName
            }
        },

        async getAvailableSites() {
            const t = this

            const sitesResult = await t.MIX_readDocuments("sites")
            if (sitesResult.code === 1) {
                t.availableSites = sitesResult.data
            } else {
                t.availableSites = []
            }
        },

        async updateSwappStatus() {
            const t = this

            console.log('here')

            t.form.swappMethod = "Admin"
            if (t.form.swappStatus === 0) {
                t.swappDirection = 1
                t.form.swappStatus = 1
            } else {
                t.swappDirection = 0
                t.form.swappStatus = 0
            }

            const updateDocumentResult = await t.MIX_updateDocument("users", t.form)

            if (updateDocumentResult.code === 1) {
                t.modifySwappStatus = false
                t.MIX_alert(1, "Swapp Status Updated", updateDocumentResult.data, null)
                const user = await t.MIX_readDocumentById("users", t.form.id)
                t.ACT_openStaffDirectory(user.data)
            } else {
                t.MIX_alert(-1, "ERROR - Could not update Status", null, updateDocumentResult.error)
            }
        },

        async getAvailableLocations() {
            const t = this

            const locationsResult = await t.MIX_readDocumentsByKeyValue("locations", "site.id", t.selectedSite.id)
            if (locationsResult.code === 1) {
                t.availableLocations = locationsResult.data
            } else {
                t.availableLocations = []
            }
        },

        getLocationsHistory() {
            const t = this

            t.$firebase.db
                .collection("swappLogs")
                .where("swappedUser", "==", t.form.id)
                .onSnapshot(function (querySnapshot) {
                    t.locationsHistory = []
                    querySnapshot.forEach(function (doc) {
                        var document = doc.data()
                        document.id = doc.id
                        if (!document.hasOwnProperty("delete")) {
                            t.locationsHistory.push(document)
                        } else {
                            // Do not Show Notice
                        }
                    })
                })
        },

        /**
         * Upload Profile Image
         *
         * Upon successful saving/updating of a User document, also save the profile image path.
         *
         * @returns {Promise<void>}
         */
        async uploadProfileImage() {
            const t = this

            if (t.photoUploadResult !== {}) {

                // Save to the document with: collection | user id | image path
                const updatePhotosResult = await this.MIX_updateDocumentFieldsById(
                    'users', t.photoUploadResult.docLink, {profilePicFileURL: t.photoUploadResult.fileURL})

                // Render confirmation box
                if (updatePhotosResult.code === 1) {
                    this.MIX_alert(1, 'User successfully updated', updatePhotosResult.data, null)
                } else {
                    this.MIX_alert(-1, 'Error updating user', null, updatePhotosResult.error)
                }
            }
        },

        /**
         * Handle User DOB Date Picker
         *
         * Takes the time emitted from DatePicker and assigns it to the form data.
         *
         * @param date
         */
        handleUserDOBPicker(date) {
            const t = this

            // Convert to Unix timestamp
            date = t.$moment(date).format('x')
            t.form.userDOB = date
        },

        /**
         * Handle User DOB Date Picker
         *
         * Takes the time emitted from DatePicker and assigns it to the form data.
         *
         */
        handleUserStartDatePicker(date) {
            const t = this

            // Convert to Unix timestamp
            date = t.$moment(date).format('x')
            t.form.userStartDate = date
        },

        /**
         * Emitted Created Site Permit
         *
         * If the site permit has been successfully created, call editItem() to reset the form.
         *
         * @param createdPermit boolean - if the site permit was created
         */
        emittedCreatedSitePermit(createdPermit) {
            const t = this

            if (createdPermit) {
                t.editItem()
            }
        },

    },

    watch: {

        GET_confirmBoxResult: {
            handler: async function () {
                const t = this

                if (t.GET_confirmBoxResult.data.collection === t.collection) {
                    if (t.GET_confirmBoxResult.data.result === "YES") {
                        var deleteResult = await t.MIX_deleteDocument(t.GET_confirmBoxResult.data.collection, t.GET_confirmBoxResult.data.id)
                        if (deleteResult.code === 1) {
                            t.MIX_alert(1, `${t.collectionItemTitle} and Training Deleted`, deleteResult.data, null)
                            t.ACT_panelLayout({leftPanel: 12, rightPanel: 0, show: false, dynamicComponent: ""})
                        } else {
                            t.MIX_alert(-1, `Error Deleting ${t.collectionItemTitle}`, null, deleteResult.error)
                        }
                    }
                } else if (t.GET_confirmBoxResult.data.collection === "training") {
                    if (t.GET_confirmBoxResult.data.result === "YES") {
                        const updateResult = await t.MIX_updateDocumentFieldsById("users", t.form.id, {trainingCurrent: t.form.trainingCurrent})
                        if (updateResult.code === 1) {
                            t.MIX_alert(1, `Training deleted`, updateResult.data, null)
                            t.refreshItemView()
                        } else {
                            t.MIX_alert(-1, "Error deleteting training", null, updateResult.error)
                        }
                    }
                } else {
                    // Do nothing
                }
            },
            deep: true,
        },

        /**
         * Photo Upload Result
         *
         * On upload of a profile picture, add its storage path to the photoUploadResult variable.
         */
        GET_photoUploadResult: {
            handler: async function () {
                const t = this

                t.photoUploadResult = t.GET_photoUploadResult
                t.tempImage = t.photoUploadResult.fileURL

            }, deep: true
        },
    },

    async mounted() {
        const t = this

        t.removeUploadedDocument()

        const openItem = await t.GET_openItem
        t.formMode = openItem.process

        if (openItem.process === "New") {
            t.formReadOnly = false
            t.formBackground = "white"
        } else {
            t.form = openItem.data
            t.formReadOnly = true
            t.formBackground = "grey lighten-3"
        }
        if (openItem.popup === false) {

        }

        t.getLocationsHistory()

        // Get collections data
        await t.getSitesCollectionData()
        await t.getContractorsCollectionData()
    },

};
</script>

<style scoped>
.disabled {
    pointer-events: none;
    cursor: not-allowed;
}

.profileImage-container {
    border: 1px solid lightgray !important;
}

.expandtofill {
    display: block !important;
    width: 100% !important;
    height: 100% !important;
    /* background: #ffffff !important; */
}
</style>
